import { Box, Paper, Stack, Typography } from "@mui/material"
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { TSale } from "../../../../core/types/Sale"
import TextFieldRequired from "../../../Inputs/TextFieldRequired"
import Validations from "../../../../core/functions/validations"
import { useEffect } from "react"
import { formatAsYouType, isValidPhone, ensurePhoneEngine } from "../../../../core/phone"
import { useBuyerCountryPhoneSync } from "../../../../core/hooks/useBuyerCountryPhoneSync"
import {
  CountrySelect,
  COUNTRY_SELECT_LABEL_OFFSET,
} from "../../../Inputs/CountrySelect"
import { useCheckoutContext } from "../../../../core/hooks/useCheckoutContext"
import { useGatewayContext } from "../../../../core/contexts/GatewayContext"
import { useTranslation } from "react-i18next"

interface ICheckoutClient7 {
  control: Control<TSale, any>
  setValue: UseFormSetValue<TSale>
  watch: UseFormWatch<TSale>
  saveLostSale: () => void
  clearErrors: () => void
}

const CheckoutClient7 = ({
  control,
  setValue,
  watch,
  saveLostSale,
  clearErrors,
}: ICheckoutClient7) => {
  const { productPrice } = useCheckoutContext()
  const { useStripe: useStripePayment } = useGatewayContext()
  const { t } = useTranslation()

  useBuyerCountryPhoneSync(watch, setValue)

  const phoneValue = watch("phone")
  const phoneCountry = watch("phoneCountry") ?? "BR"

  // Carrega o motor de numeração quando o país deixa de ser BR.
  // BR nunca carrega — máscara síncrona própria.
  useEffect(() => {
    ensurePhoneEngine(phoneCountry)
  }, [phoneCountry])

  const customFields = productPrice?.customFields?.isCustomFields
    ? productPrice?.customFields?.fields.filter((field) => field.isChecked) ??
      []
    : []

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid #DCDDDE",
        borderRadius: "20px",
        backgroundColor: "#fbfbfb",
      }}
    >
      <Stack direction="column" spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 0.5 }}>
          <img src="/assets/layouts/v7/user.png" alt="user" />
          <Typography
            fontSize="28px"
            fontWeight={500}
            lineHeight="32px"
            color="#3ebf90"
          >
            {t("checkout.v7.client.contact")}
          </Typography>
        </Stack>
        <TextFieldRequired
          control={control}
          variant="rounded"
          fieldName="email"
          label={t("checkout.v7.client.email")}
          placeholder={t("checkout.v7.client.emailPlaceholder")}
          onFieldExit={saveLostSale}
          clearErrors={clearErrors}
          validate={(value) =>
            !value || value == "" ? true : Validations.email(value)
          }
          inputProps={{
            "data-cy": "customerEmail",
            "data-testid": "customerEmail",
            style: { textTransform: "lowercase" },
          }}
          dataCyErrorLabel="customerEmailError"
        />

        <TextFieldRequired
          control={control}
          variant="rounded"
          fieldName="name"
          label={t("checkout.v7.client.name")}
          placeholder={t("checkout.v7.client.namePlaceholder")}
          onFieldExit={saveLostSale}
          clearErrors={clearErrors}
          inputProps={{
            "data-cy": "customerName",
            "data-testid": "customerName",
          }}
          dataCyErrorLabel="customerNameError"
          transform={(v) => v.replace(/[0-9]/g, "")}
          validate={(v) => {
            const s = String(v ?? "")
            return /\d/.test(s) ? t("checkout.client.nameNoNumbers") : true
          }}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 2 }}
        >
          <TextFieldRequired
            control={control}
            variant="rounded"
            fieldName="phone"
            label={t("checkout.v7.client.phone")}
            shrink={phoneValue !== ""}
            inputProps={{
              inputMode: "numeric",
              "data-cy": "customerPhone",
              "data-testid": "customerPhone",
            }}
            sxInputLabel={{
              ml: phoneValue === "" ? COUNTRY_SELECT_LABEL_OFFSET : 3.5,
            }}
            dataCyErrorLabel="customerPhoneError"
            onFieldExit={saveLostSale}
            clearErrors={clearErrors}
            transform={(v: string) => formatAsYouType(v, phoneCountry)}
            validate={(value) =>
              !value || value === "" ? true : isValidPhone(value, phoneCountry)
            }
            startAdornment={
              <CountrySelect
                value={phoneCountry}
                onChange={(iso) => {
                  setValue("phoneCountry", iso)
                  setValue("phone", "")
                }}
              />
            }
          />

          <Box sx={{ display: { xs: "block", sm: "none" } }} />

          {!useStripePayment && (
            <TextFieldRequired
              control={control}
              variant="rounded"
              fieldName="document"
              label={t("checkout.v7.client.document")}
              placeholder={t("checkout.v7.client.documentPlaceholder")}
              inputProps={{
                inputMode: "numeric",
                maxLength: 18,
                "data-cy": "customerDocument",
                "data-testid": "customerDocument",
              }}
              dataCyErrorLabel="customerDocumentError"
              clearErrors={clearErrors}
              validate={(value) =>
                !value || value == "" ? true : Validations.CPF_CNPJ(value)
              }
            />
          )}
        </Stack>

        {/*<Typography fontSize="16px" lineHeight="16px" fontWeight={500}>
          👍 No Brasil, o CPF é obrigatório para compras online.
        </Typography>*/}

        {customFields.map((field, index) => (
          <TextFieldRequired
            key={field.identity}
            control={control}
            variant="labeled"
            size="small"
            fieldName={`customFields.${index}.value`}
            label={field.name}
            placeholder=""
            inputProps={{
              "data-cy": field.identity,
              "data-testid": field.identity,
            }}
            dataCyErrorLabel={field.identity}
            clearErrors={clearErrors}
            validate={(value) => (value ?? "") !== ""}
            onChangeValue={(value) => {
              setValue(`customFields.${index}`, {
                identity: field.identity,
                name: field.name,
                value,
              })
            }}
          />
        ))}
      </Stack>
    </Paper>
  )
}

export default CheckoutClient7
